import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import {
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Server,
  Archive,
  RotateCcw,
} from 'lucide-react'
import {
  StorageVolume,
  StorageVolumeKube,
  StorageVolumeBackup,
  StorageVolumeRestore,
} from '@/services/inference.gen'

type StorageMigrationStatusProps = {
  storageVolume: StorageVolume
  storageVolumeKube: StorageVolumeKube
}

export const StorageMigrationStatus = ({
  storageVolume,
  storageVolumeKube,
}: StorageMigrationStatusProps) => {
  // only show for local storage
  if (storageVolume.type_id !== 3) {
    return null
  }

  if (!storageVolumeKube) {
    return null
  }

  const storageNode = storageVolumeKube.node_name || 'unknown'

  // get all jobs
  const backups = storageVolumeKube.storage_volume_backups || []
  const restores = storageVolumeKube.storage_volume_restores || []

  // Combine and sort all jobs by creation time
  const allJobs: MigrationJob[] = [
    ...backups.map((b: StorageVolumeBackup) => ({
      type: 'backup' as const,
      status: b.status,
      progress: b.progress,
      id: b.id,
      name: b.backup_name,
      timestamp: b.created_at,
    })),
    ...restores.map((r: StorageVolumeRestore) => ({
      type: 'restore' as const,
      status: r.status,
      progress: r.progress,
      id: r.id,
      name: `restore-${r.id}`,
      timestamp: r.created_at,
    })),
  ].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )

  // Check if any job is in progress
  const hasActiveJobs = allJobs.some(
    (job) => job.status === 'running' || job.status === 'init'
  )

  // Check if migration is complete
  const hasCompletedMigration = allJobs.some(
    (job) => job.type === 'restore' && job.status === 'success'
  )

  // Only show if there's migration activity or it's a migrated volume
  if (allJobs.length === 0) {
    if (storageVolume.origin_storage_volume_id) {
      return (
        <Alert className="border-muted bg-foreground/5">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-muted-foreground" />
            <AlertDescription className="text-sm">
              Migrated volume on node:{' '}
              <span className="font-mono bg-foreground/10 rounded-sm text-xs px-2 py-0.5">
                {storageNode}
              </span>
            </AlertDescription>
          </div>
        </Alert>
      )
    }
    return null
  }

  const getBorderClass = () => {
    if (hasActiveJobs) return 'border-blue-500/50 bg-blue-500/5'
    if (hasCompletedMigration) return 'border-green-500/50 bg-green-500/5'
    if (allJobs.some((job) => job.status === 'failed'))
      return 'border-destructive/50 bg-destructive/5'
    return 'border-muted bg-foreground/5'
  }

  return (
    <Alert className={getBorderClass()}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              Migration {hasActiveJobs ? 'in progress' : 'history'}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            Node: {storageNode}
          </span>
        </div>
        <div className="space-y-2">
          {allJobs.map((job) => (
            <JobStatusItem key={`${job.type}-${job.id}`} job={job} />
          ))}
        </div>
      </div>
    </Alert>
  )
}

type MigrationJob = {
  type: 'backup' | 'restore'
  status: string
  progress: number
  id: number
  name: string
  timestamp: string
}

const JobStatusItem = ({ job }: { job: MigrationJob }) => {
  const isInProgress = job.status === 'running' || job.status === 'init'
  const hasFailed = job.status === 'failed'
  const hasSucceeded = job.status === 'success'

  const getStatusIcon = () => {
    if (hasSucceeded) return <CheckCircle2 className="h-4 w-4 text-green-500" />
    if (hasFailed) return <AlertTriangle className="h-4 w-4 text-destructive" />
    if (isInProgress)
      return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
    return job.type === 'backup' ? (
      <Archive className="h-4 w-4 text-muted-foreground" />
    ) : (
      <RotateCcw className="h-4 w-4 text-muted-foreground" />
    )
  }

  const getStatusText = () => {
    if (isInProgress) {
      return job.type === 'backup'
        ? `Creating backup... ${job.progress}%`
        : `Restoring data... ${job.progress}%`
    }
    if (hasSucceeded) {
      return job.type === 'backup' ? 'Backup completed' : 'Restore completed'
    }
    if (hasFailed) {
      return job.type === 'backup' ? 'Backup failed' : 'Restore failed'
    }
    return 'Pending'
  }

  return (
    <div className="flex items-start gap-2">
      {getStatusIcon()}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{getStatusText()}</span>
        </div>
        {isInProgress && job.progress > 0 && (
          <Progress value={job.progress} className="h-1 mt-1" />
        )}
      </div>
    </div>
  )
}
